import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import useSWR from "swr";
import { Pagination, Flex, Text } from "@mantine/core";
import { fetcher } from "../../../services/fetcher";
import { websiteFetcher } from "../../../features/website/services/fetcher";
import useFilters from "../../hooks/useFilters/useFilters";
import DataTable from "../DataTable/DataTable";
import {
  SEARCH_DEBOUNCE_MS,
  useDebouncedSearchQuery,
} from "../../hooks/useDebouncedSearchQuery/useDebouncedSearchQuery";

const EMPTY_SWR_CONFIG = Object.freeze({});
const BASE_SWR_CONFIG = Object.freeze({ keepPreviousData: true });

const toRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const DataWrapper = ({
  endpoint,
  /** When set, this base URL is used (with `?search=`) instead of `endpoint` while the user has typed a search query. */
  searchEndpoint,
  /** Optional row mapper for API results before passing to DataTable (e.g. operators list). */
  mapResults,
  config,
  filterOptions,
  swrConfig = EMPTY_SWR_CONFIG,
  onSelectHandler,
  useWebsiteFetcher = false,
  onData,
  rows,
  toolbarLeftContent,
  toolbarRightContent,
  toolbarBottomContent,
  additionalFilterQuery = "",
  searchPlaceholder,
  onSearchChange,
  searchDebounceMs = SEARCH_DEBOUNCE_MS,
  filterRows,
}) => {
  const [pageIndex, setPageIndex] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [filterQuery, setFilterQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [orderingValue, setOrderingValue] = useState("");

  const { apiSearchQuery, isSearchPending } = useDebouncedSearchQuery(
    searchInputValue,
    searchDebounceMs
  );

  const memoizedConfig = useMemo(() => config, [config]);
  const memoizedFilterOptions = useMemo(() => filterOptions, [filterOptions]);
  const mergedSwrConfig = useMemo(
    () => ({ ...BASE_SWR_CONFIG, ...swrConfig }),
    [swrConfig]
  );

  const isClientMode = Array.isArray(rows);

  const filterSignature = useMemo(
    () => `${filterQuery}|${additionalFilterQuery}|${orderingValue}`,
    [filterQuery, additionalFilterQuery, orderingValue]
  );

  const prevFilterSignatureRef = useRef(filterSignature);
  const prevApiSearchRef = useRef(apiSearchQuery);
  const filtersChanged = prevFilterSignatureRef.current !== filterSignature;
  const searchChanged = prevApiSearchRef.current !== apiSearchQuery;
  const requestPage = filtersChanged || searchChanged ? 1 : pageIndex;

  if (filtersChanged) {
    prevFilterSignatureRef.current = filterSignature;
  }
  if (searchChanged) {
    prevApiSearchRef.current = apiSearchQuery;
  }

  const fullUrl = useMemo(() => {
    if (isClientMode) return null;

    const baseUrl = apiSearchQuery && searchEndpoint ? searchEndpoint : endpoint;
    const [path, existingQuery = ""] = baseUrl.split("?");
    const queryParams = new URLSearchParams(existingQuery);

    if (filterQuery) {
      const filterParams = new URLSearchParams(filterQuery.slice(1));
      filterParams.forEach((value, key) => {
        queryParams.set(key, value);
      });
    }

    if (apiSearchQuery) {
      queryParams.set("search", apiSearchQuery);
    } else {
      queryParams.delete("search");
    }

    if (orderingValue) {
      queryParams.set("ordering", orderingValue);
    } else {
      queryParams.delete("ordering");
    }

    if (additionalFilterQuery) {
      const extra = new URLSearchParams(additionalFilterQuery.replace(/^\?/, ""));
      extra.forEach((value, key) => {
        queryParams.set(key, value);
      });
    }

    queryParams.set("page", requestPage);

    const queryString = queryParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  }, [
    isClientMode,
    apiSearchQuery,
    searchEndpoint,
    endpoint,
    filterQuery,
    orderingValue,
    additionalFilterQuery,
    requestPage,
  ]);

  const swrKey = useMemo(() => {
    if (isClientMode) return null;
    if (isSearchPending) return null;
    return fullUrl;
  }, [isClientMode, isSearchPending, fullUrl]);

  const selectedFetcher = useWebsiteFetcher ? websiteFetcher : fetcher;

  const { form, onSubmit } = useFilters({
    onFilterChange: useCallback((query) => {
      setFilterQuery(query);
    }, []),
  });

  useEffect(() => {
    if (requestPage === 1 && pageIndex !== 1) {
      setPageIndex(1);
    }
  }, [apiSearchQuery, filterSignature, requestPage, pageIndex]);

  const {
    data,
    error: pageError,
    isLoading,
    isValidating,
  } = useSWR(swrKey, selectedFetcher, mergedSwrConfig);

  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  useEffect(() => {
    onDataRef.current?.(data);
  }, [data]);

  useEffect(() => {
    onSearchChange?.(apiSearchQuery);
  }, [apiSearchQuery, onSearchChange]);

  useEffect(() => {
    if (isClientMode) {
      const configuredPageSize = Number(memoizedConfig?.itemsPerPage);
      if (Number.isFinite(configuredPageSize) && configuredPageSize > 0) {
        setItemsPerPage(configuredPageSize);
      }
      const totalItems = rows.length;
      setTotalPages(Math.max(1, Math.ceil(totalItems / itemsPerPage)));
      return;
    }

    if (data) {
      const resultRows = toRows(data);
      const totalItems = Number(data?.count ?? resultRows.length) || 0;
      const serverPageSize = Number(data?.page_size ?? data?.pageSize);

      if (Number.isFinite(serverPageSize) && serverPageSize > 0) {
        setItemsPerPage(serverPageSize);
      }

      setTotalPages(Math.max(1, Math.ceil(totalItems / itemsPerPage)));
    }
  }, [
    data,
    isClientMode,
    itemsPerPage,
    memoizedConfig?.itemsPerPage,
    pageIndex,
    rows,
  ]);

  const handlePageChange = (page) => {
    startTransition(() => {
      setPageIndex(page);
    });
  };

  const handleSearch = (event) => {
    setSearchInputValue(event.target.value);
  };

  const handleOrdering = (value) => {
    startTransition(() => {
      setOrderingValue(value);
    });
  };

  const handleSelectionChange = (newSelection) => {
    onSelectHandler?.(newSelection);
  };

  if (pageError) {
    return <p>Something went wrong</p>;
  }

  const rawResults = isClientMode ? rows : toRows(data);
  const mappedRows =
    !data && isLoading && !isClientMode
      ? []
      : mapResults
        ? mapResults(rawResults)
        : rawResults;
  const filteredRows =
    typeof filterRows === "function"
      ? mappedRows.filter(filterRows)
      : mappedRows;
  const paginatedRows = isClientMode
    ? filteredRows.slice((pageIndex - 1) * itemsPerPage, pageIndex * itemsPerPage)
    : filteredRows.length > itemsPerPage
      ? filteredRows.slice((pageIndex - 1) * itemsPerPage, pageIndex * itemsPerPage)
      : filteredRows;
  const tableRows = paginatedRows;

  const totalRecords =
    Number(isClientMode ? rawResults.length : data?.count ?? rawResults.length) || 0;
  const rangeFrom = totalRecords ? (pageIndex - 1) * itemsPerPage + 1 : 0;
  const rangeTo = totalRecords ? Math.min(pageIndex * itemsPerPage, totalRecords) : 0;
  const showTableLoading =
    (isLoading && !data && !isClientMode) || isSearchPending || (isValidating && !isSearchPending);

  return (
    <>
      <DataTable
        data={tableRows}
        config={memoizedConfig}
        filterOptions={memoizedFilterOptions}
        loading={showTableLoading}
        form={form}
        onSubmit={onSubmit}
        onSearch={handleSearch}
        searchValue={searchInputValue}
        onOrdering={handleOrdering}
        orderingValue={orderingValue}
        onSelectionChange={handleSelectionChange}
        coverTitle="Cover Title"
        toolbarPy={memoizedConfig.toolbarPy ?? "xl"}
        toolbarLeftContent={toolbarLeftContent}
        toolbarRightContent={toolbarRightContent}
        toolbarBottomContent={toolbarBottomContent}
        searchPlaceholder={searchPlaceholder}
      />

      <Flex
        justify={memoizedConfig.hideFooterRecordCount ? "flex-end" : "space-between"}
        align="center"
        mt="lg"
        px="sm"
      >
        {!memoizedConfig.hideFooterRecordCount && !isLoading && (isClientMode || data) && (
          <Text size="sm" fw="bold" c="dimmed">
            {memoizedConfig.footerRangeFormat
              ? `Showing ${rangeFrom}-${rangeTo} of ${totalRecords.toLocaleString("en-IN")}`
              : `Showing ${tableRows.length} of ${totalRecords.toLocaleString("en-IN")} records`}
          </Text>
        )}
        <Pagination
          page={pageIndex}
          onChange={handlePageChange}
          total={totalPages}
          defaultValue={pageIndex}
          siblings={1}
        />
      </Flex>
    </>
  );
};

export default DataWrapper;
